export interface PollerOptions<T> {
  callback(): Promise<T>
  interval: number
  limit?: LimitOptions
  delayedStartMs?: number
  shouldEndPolling(data: T): boolean
}

interface LimitOptions {
  count: number
  shouldThrowErrorOnLimitReached?: boolean
}

interface PollerInstance<T> {
  start(): Promise<T | null>
  stop(): void
}

async function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * Creates instance of poller, which could be started or stopped imperatively or by provided condition.
 * Runs callback on specified interval until shouldEndPolling return true or when limit reached.
 * @param pollerOptions Poller options
 * @param pollerOptions.callback Poller callback
 * @param pollerOptions.interval Interval between poller calls
 * @param pollerOptions.limit Maximum number of calls for the poller
 * @param pollerOptions.shouldEndPolling Callback, which calls on every poll. When returns true, polling stops
 * @param pollerOptions.delayedStartMs If presents, poller will start work only after specified ms
 */
export function createPoller<T>({
  callback,
  interval,
  limit,
  delayedStartMs,
  shouldEndPolling,
}: PollerOptions<T>): PollerInstance<T> {
  let interruptPolling = false

  const start = async (): Promise<T | null> => {
    let counter = 0
    return new Promise((resolve, reject) => {
      const setTimer = async (): Promise<void> => {
        const timeoutId = setTimeout(async () => {
          if (interruptPolling) {
            resolve(null)
            clearTimeout(timeoutId)
            return
          }
          if (limit && counter >= limit.count) {
            if (limit.shouldThrowErrorOnLimitReached) {
              reject(new Error('Poll limit reached'))
            } else {
              resolve(null)
            }
            return
          }

          try {
            const data = await callback()
            counter += 1
            if (shouldEndPolling(data)) {
              resolve(data)
            } else {
              setTimer()
            }
          } catch (e) {
            reject(e)
          }
        }, interval)
      }

      async function run(): Promise<void> {
        if (delayedStartMs) {
          await delay(delayedStartMs)
          setTimer()
        } else {
          setTimer()
        }
      }

      run()
    })
  }

  const stop = (): void => {
    interruptPolling = true
  }

  return { start, stop }
}
